import { useState } from 'react';
import { Button, Input, Label, TextField, toast } from '@heroui/react';
import { Segment } from '@heroui-pro/react/segment';
import { Sheet } from '@heroui-pro/react/sheet';
import { Download, Pencil, Trash2 } from 'lucide-react';
import {
  agreementsSemaphore,
  validateAgreementText,
  weekNumber,
  type Agreement,
  type Light,
} from '@odd/domain';
import { apiRaw } from '../lib/api';
import { useDeleteAgreement, useUpdateAgreement } from '../lib/queries';
import { fmtShort } from '../lib/format';
import { useBoardCtx } from './BoardContext';
import { ConfirmDialog, Panel, Select } from './ui';

const BAR: Record<Light, string> = { verde: 'bg-ok', ambar: 'bg-warn', rojo: 'bg-bad' };

function PreviousRow({ a }: { a: Agreement }) {
  const { officeId } = useBoardCtx();
  const update = useUpdateAgreement(officeId);
  return (
    <li className="grid gap-2 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-ink">
          <span className="tabular mr-2 text-ink-3">{a.code}</span>
          {a.text}
        </div>
        <div className="text-xs text-ink-3">
          {a.responsibleName} · plazo {fmtShort(a.dueDate)}
        </div>
      </div>
      <Segment
        size="sm"
        aria-label={`Estado del acuerdo ${a.code}`}
        selectedKey={a.status ?? ''}
        onSelectionChange={(k) => update.mutate({ id: a.id, status: String(k) as Light })}
        isDisabled={update.isPending}
      >
        <Segment.Item id="verde">● verde</Segment.Item>
        <Segment.Item id="ambar">● ámbar</Segment.Item>
        <Segment.Item id="rojo">● rojo</Segment.Item>
      </Segment>
    </li>
  );
}

/** Edición de un acuerdo en un Sheet lateral: texto (infinitivo), responsable único y plazo */
function AgreementEditSheet({ a, onClose }: { a: Agreement | null; onClose: () => void }) {
  return (
    <Sheet isOpen={!!a} onOpenChange={(o) => !o && onClose()} placement="right">
      <Sheet.Backdrop>
        <Sheet.Content className="w-full max-w-lg">
          <Sheet.Dialog>
            <Sheet.CloseTrigger />
            {a && <AgreementEditForm key={a.id} a={a} onDone={onClose} />}
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}

function AgreementEditForm({ a, onDone }: { a: Agreement; onDone: () => void }) {
  const { board, officeId } = useBoardCtx();
  const update = useUpdateAgreement(officeId);
  const [text, setText] = useState(a.text);
  const [resp, setResp] = useState(a.responsibleId);
  const [due, setDue] = useState(a.dueDate);
  const [error, setError] = useState<string | null>(null);

  // El responsable actual puede no estar ya en la lista de la oficina: se mantiene como opción
  const people = board.people.some((p) => p.id === a.responsibleId)
    ? board.people
    : [{ id: a.responsibleId, name: a.responsibleName }, ...board.people];

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const err =
      validateAgreementText(text) ??
      (!resp ? 'Falta el responsable.' : !due ? 'Falta la fecha concreta.' : null);
    setError(err);
    if (err) return;
    const person = people.find((p) => p.id === resp)!;
    update.mutate(
      { id: a.id, text: text.trim(), responsibleId: person.id, responsibleName: person.name, dueDate: due },
      {
        onSuccess: () => {
          toast.success('Acuerdo actualizado');
          onDone();
        },
        onError: (e2) => setError(e2.message),
      },
    );
  };

  return (
    <>
      <Sheet.Header>
        <div className="text-xs font-bold tracking-wider text-orange-ink uppercase">Acuerdo {a.code}</div>
        <Sheet.Heading className="text-xl font-extrabold text-ink">Editar acuerdo</Sheet.Heading>
        {a.caseLabel && <p className="text-sm text-ink-3">{a.caseLabel}</p>}
      </Sheet.Header>
      <Sheet.Body className="pb-8">
        <form onSubmit={save} className="grid gap-4" noValidate>
          <TextField value={text} onChange={setText} isInvalid={!!error && !!validateAgreementText(text)}>
            <Label>Acuerdo, en infinitivo</Label>
            <Input autoFocus placeholder="Escalar a CSS la peritación del siniestro" />
          </TextField>
          <div className="grid gap-1.5">
            <span className="text-sm font-medium text-ink">Responsable único</span>
            <Select
              label="Responsable"
              value={resp}
              onChange={setResp}
              options={people.map((p) => ({ value: p.id, label: p.name, description: p.role ?? undefined }))}
            />
          </div>
          <TextField value={due} onChange={setDue}>
            <Label>Plazo</Label>
            <Input type="date" />
          </TextField>
          <div className="rounded-xl bg-card-2 px-3 py-2 text-xs text-ink-2">
            Un responsable · fecha concreta · empieza por un verbo en infinitivo («Enviar», «Llamar»,
            «Escalar»…).
          </div>
          {error && (
            <p role="alert" className="text-sm font-semibold text-bad">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" isPending={update.isPending}>
              Guardar cambios
            </Button>
            <Button variant="ghost" onPress={onDone}>
              Cancelar
            </Button>
          </div>
        </form>
      </Sheet.Body>
    </>
  );
}

/** Acuerdo de esta sesión: editar (Sheet) o eliminar (con confirmación) */
function CurrentRow({ a, onEdit }: { a: Agreement; onEdit: () => void }) {
  const { officeId } = useBoardCtx();
  const del = useDeleteAgreement(officeId);
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="flex items-start justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-ink">
          <span className="tabular mr-2 text-ink-3">{a.code}</span>
          {a.text}
        </div>
        <div className="text-xs text-ink-3">
          {a.responsibleName} · {fmtShort(a.dueDate)}
          {a.caseLabel ? ` · ${a.caseLabel}` : ''}
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button
          size="sm"
          variant="ghost"
          isIconOnly
          aria-label={`Editar el acuerdo ${a.code}`}
          onPress={onEdit}
        >
          <Pencil className="size-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          isIconOnly
          aria-label={`Eliminar el acuerdo ${a.code}`}
          onPress={() => setConfirming(true)}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        title={`¿Eliminar el acuerdo ${a.code}?`}
        confirmLabel="Eliminar acuerdo"
        isPending={del.isPending}
        onConfirm={() =>
          del.mutate(a.id, {
            onSuccess: () => {
              setConfirming(false);
              toast.success('Acuerdo eliminado');
            },
            onError: (e) => toast.danger(e.message),
          })
        }
      >
        «{a.text}» · {a.responsibleName} · {fmtShort(a.dueDate)}. No se puede deshacer.
      </ConfirmDialog>
    </li>
  );
}

export function Agreements({ currentAnchor }: { currentAnchor?: string } = {}) {
  const { board, officeId } = useBoardCtx();
  const [editing, setEditing] = useState<Agreement | null>(null);
  const prev = board.previousAgreements;
  const s = agreementsSemaphore(prev);
  const total = prev.length || 1;

  const exportMd = async () => {
    try {
      const res = await apiRaw(`/api/offices/${officeId}/agreements/export?week=${board.week}&format=md`);
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `acuerdos-${board.week}.md`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.danger('No se pudo exportar');
    }
  };

  return (
    <>
      <div className="agreements-grid grid gap-4 lg:grid-cols-2">
        <Panel
          title={
            prev[0] ? `Acuerdos de la semana ${weekNumber(prev[0].week)}` : 'Acuerdos de la semana anterior'
          }
          description={
            s.pctOnTime != null
              ? `${s.pctOnTime} % cumplidos en plazo`
              : 'Se lee el semáforo. No se cuentan novedades.'
          }
        >
          {prev.length === 0 ? (
            <p className="text-sm text-ink-3">No hay acuerdos registrados de la semana anterior.</p>
          ) : (
            <>
              <div className="flex h-2.5 overflow-hidden rounded-full bg-band" aria-hidden>
                {(['verde', 'ambar', 'rojo'] as const).map((k) => (
                  <span key={k} className={BAR[k]} style={{ width: `${(s[k] / total) * 100}%` }} />
                ))}
              </div>
              <div className="mt-2 flex gap-4 text-xs text-ink-2">
                <span>
                  <b className="text-ink">{s.verde}</b> verde
                </span>
                <span>
                  <b className="text-ink">{s.ambar}</b> ámbar
                </span>
                <span>
                  <b className="text-ink">{s.rojo}</b> rojo
                </span>
                {s.pendiente > 0 && (
                  <span>
                    <b className="text-ink">{s.pendiente}</b> sin valorar
                  </span>
                )}
              </div>
              <ul className="mt-2 divide-y divide-line">
                {prev.map((a) => (
                  <PreviousRow key={a.id} a={a} />
                ))}
              </ul>
            </>
          )}
        </Panel>

        <Panel
          id={currentAnchor}
          title="Acuerdos de esta sesión"
          description="Un responsable · fecha concreta · en infinitivo"
          action={
            board.currentAgreements.length > 0 && (
              <Button size="sm" variant="secondary" onPress={exportMd}>
                <Download className="size-4" /> Exportar para el acta
              </Button>
            )
          }
        >
          {board.currentAgreements.length === 0 ? (
            <p className="text-sm text-ink-3">
              Aún no hay acuerdos. Abre un caso de la mesa y registra la decisión.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {board.currentAgreements.map((a) => (
                <CurrentRow key={a.id} a={a} onEdit={() => setEditing(a)} />
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <AgreementEditSheet a={editing} onClose={() => setEditing(null)} />
    </>
  );
}
